import React, { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import useFormCondition from '../../../../Hooks/useFormCondition';
import useFormName from '../../../../../../Hooks/useFormName';
import { Rule } from '../../../../Enums/Rule.enum';

interface FormConditionProps {
	controlName: string;
	equal?: string | boolean;
	in?: string[] | number[];
	children?: any;
	rules?: FormConditionRule[];
	resetValuesOnConditionMet?: boolean;
}

export interface FormConditionRule {
	formControlKey: string;
	rule: Rule;
	value: any;
}

export const normalizeConditionValue = (value?: string | boolean | number) => {
	if (value === '-1') return false;
	if (value === '1') return true;
	return value;
};

export const extractConditionValue = (value: any) =>
	value?.value?.code ?? value?.value ?? value;

const clearLike = (current: Record<string, unknown>) =>
	Object.fromEntries(
		Object.entries(current).map(([k, v]) => {
			if (typeof v === 'string') return [k, ''];
			if (typeof v === 'boolean') return [k, false];
			if (Array.isArray(v)) return [k, []];
			if (v && typeof v === 'object') return [k, null];
			return [k, undefined];
		}),
	) as Record<string, unknown>;

const FormCondition = (props: FormConditionProps) => {
	const {
		controlName,
		children,
		in: pin,
		rules,
		resetValuesOnConditionMet,
	} = props;

	const equal = normalizeConditionValue(props.equal);

	const { fieldName } = useFormName({ name: controlName });
	const { result } = useFormCondition({ rules });

	const { watch, reset, getValues, setValue } = useFormContext();

	const watchedValue = watch(fieldName);
	const rValue = normalizeConditionValue(extractConditionValue(watchedValue));

	const hasEqual = typeof equal !== 'undefined';
	const isEqualMatch = hasEqual ? rValue === equal : true;

	const hasPin = typeof pin !== 'undefined';
	// @ts-ignore
	const isPinMatch = hasPin ? pin.indexOf(rValue) !== -1 : true;

	const isMet = !!result && isEqualMatch && isPinMatch;

	const prevIsMetRef = useRef<boolean>(false);
	const prevEqualMatchRef = useRef<boolean>(isEqualMatch);

	const baselineValuesRef = useRef<Record<string, unknown> | null>(null);

	useEffect(() => {
		if (!resetValuesOnConditionMet) {
			prevIsMetRef.current = isMet;
			prevEqualMatchRef.current = isEqualMatch;
			return;
		}

		if (isMet && !prevIsMetRef.current) {
			prevIsMetRef.current = true;
			prevEqualMatchRef.current = isEqualMatch;

			if (!baselineValuesRef.current) {
				const snapshotRaw = getValues() as Record<string, unknown>;
				baselineValuesRef.current = {
					...snapshotRaw,
					[fieldName]: rValue,
				};
			}

			const current = getValues() as Record<string, unknown>;
			const cleared = clearLike(current);

			const nextValues: Record<string, unknown> = {
				...cleared,
				[fieldName]: rValue,
			};

			reset(nextValues);

			setValue(fieldName, rValue as any, {
				shouldDirty: true,
				shouldTouch: true,
				shouldValidate: false,
			});

			return;
		}

		if (hasEqual && !isEqualMatch && prevEqualMatchRef.current) {
			prevIsMetRef.current = false;
			prevEqualMatchRef.current = false;

			const baseline = baselineValuesRef.current;

			if (baseline) {
				const restoreValues: Record<string, unknown> = {
					...baseline,
					[fieldName]: rValue,
				};

				reset(restoreValues);

				setValue(fieldName, rValue as any, {
					shouldDirty: true,
					shouldTouch: true,
					shouldValidate: false,
				});

				baselineValuesRef.current = null;
				return;
			}

			reset();
			return;
		}

		if (!isMet) prevIsMetRef.current = false;
		prevEqualMatchRef.current = isEqualMatch;
	}, [
		isMet,
		isEqualMatch,
		hasEqual,
		resetValuesOnConditionMet,
		getValues,
		reset,
		setValue,
		fieldName,
		rValue,
	]);

	if (!isMet) return null;

	return <div>{children}</div>;
};

export default FormCondition;
